import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { supabase } from '@/lib/supabaseClient';
import PlanModal from './components/PlanModal';
import PlanModulesSection from '@/pages/sa/billing/components/PlanModulesSection';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  price_monthly: number;
  max_users: number;
  max_contracts: number;
  storage_mb: number;
  color: string;
  is_active: boolean;
}

type TabId = 'plans' | 'modules';

const SW = { background: '#ffffff', color: '#0f172a' };

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'plans',   label: 'Plans',              icon: 'ri-price-tag-3-line' },
  { id: 'modules', label: 'Modules per Plan',   icon: 'ri-grid-line' },
];

const PLAN_ICONS: Record<string, { icon: string; bg: string; text: string; border: string; badge: string; badgeText: string }> = {
  free:       { icon: 'ri-leaf-line',        bg: 'bg-slate-50',    text: 'text-slate-500',   border: 'border-slate-200',   badge: 'bg-slate-100',   badgeText: 'text-slate-500' },
  pro:        { icon: 'ri-rocket-line',      bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   badge: 'bg-amber-50',    badgeText: 'text-amber-700' },
  enterprise: { icon: 'ri-vip-crown-line',   bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-50',  badgeText: 'text-emerald-700' },
};

const DEFAULT_STYLE = { icon: 'ri-price-tag-3-line', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-50', badgeText: 'text-blue-700' };

const storageLabel = (mb: number) => mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB` : `${mb} MB`;
const unlimUsers = (n: number) => n >= 999 ? 'Ilimitado' : `${n} users`;
const unlimContracts = (n: number) => n >= 9999 ? 'Ilimitado' : `${n} contracts`;

export default function SAPlansPage() {
  const navigate = useNavigate();
  const [loading, setLoading]       = useState(true);
  const [plans, setPlans]           = useState<Plan[]>([]);
  const [activeTab, setActiveTab]   = useState<TabId>('plans');
  const [modalPlan, setModalPlan]   = useState<Plan | null | undefined>(undefined);
  // undefined = closed, null = new, Plan = edit

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await loadPlans();
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadPlans = useCallback(async () => {
    const { data } = await supabase.from('saas_plans').select('*').order('price_monthly');
    setPlans((data ?? []) as Plan[]);
    setLoading(false);
  }, []);

  const handleToggle = async (plan: Plan) => {
    const action = plan.is_active ? 'desactivar' : 'activar';
    const result = await Swal.fire({
      title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} plan "${plan.name}"?`,
      text: plan.is_active
        ? 'Los tenants actuales no serán afectados, pero el plan dejará de estar disponible.'
        : 'El plan volverá a estar disponible para suscripción.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sí, ${action}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: plan.is_active ? '#ef4444' : '#10b981',
      ...SW,
    });
    if (!result.isConfirmed) return;

    const { error } = await supabase
      .from('saas_plans')
      .update({ is_active: !plan.is_active, updated_at: new Date().toISOString() })
      .eq('id', plan.id);

    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    await loadPlans();
    Swal.fire({ icon: 'success', title: `Plan ${plan.is_active ? 'desactivado' : 'activado'}`, timer: 1200, showConfirmButton: false, ...SW });
  };

  const handleSaved = async () => {
    setModalPlan(undefined);
    await loadPlans();
  };

  if (loading) return (
    <SuperAdminLayout>
      <SAHeader title="Plans" subtitle="Gestión de planes de suscripción" />
      <LoadingState />
    </SuperAdminLayout>
  );

  const activePlans = plans.filter((p) => p.is_active).length;

  return (
    <SuperAdminLayout>
      <SAHeader
        title="Plans"
        subtitle="Crea y gestiona planes de suscripción con precios y límites"
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total planes', value: plans.length, icon: 'ri-price-tag-3-line', color: 'text-blue-600 bg-blue-50' },
            { label: 'Activos',      value: activePlans,   icon: 'ri-checkbox-circle-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Inactivos',    value: plans.length - activePlans, icon: 'ri-close-circle-line', color: 'text-red-500 bg-red-50' },
            {
              label: 'MRR potencial',
              value: `$${plans.filter(p => p.is_active).reduce((acc, p) => acc + Number(p.price_monthly), 0)}/mo`,
              icon: 'ri-money-dollar-circle-line',
              color: 'text-amber-600 bg-amber-50',
            },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-500 text-xs font-medium">{stat.label}</p>
                <span className={`w-7 h-7 flex items-center justify-center rounded-lg ${stat.color}`}>
                  <i className={`${stat.icon} text-sm`}></i>
                </span>
              </div>
              <p className="text-slate-900 text-xl font-bold">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap cursor-pointer border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-blue-600 border-blue-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={`${tab.icon} text-sm`}></i></span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Plans ── */}
        {activeTab === 'plans' && (
          <div className="space-y-6">
            {/* Action bar */}
            <div className="flex items-center justify-between">
              <p className="text-slate-500 text-sm">{plans.length} planes configurados</p>
              <button
                onClick={() => setModalPlan(null)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
                Nuevo Plan
              </button>
            </div>

            {/* Cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const s = PLAN_ICONS[plan.id] ?? DEFAULT_STYLE;
                return (
                  <div key={plan.id} className={`bg-white rounded-2xl border ${s.border} overflow-hidden`}>
                    {/* Card header */}
                    <div className={`px-5 py-4 ${s.bg}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`w-9 h-9 flex items-center justify-center rounded-xl border ${s.border} bg-white ${s.text}`}>
                            <i className={`${s.icon}`}></i>
                          </span>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`text-sm font-bold ${s.text}`}>{plan.name}</h3>
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${s.badge} ${s.badgeText}`}>{plan.id}</span>
                            </div>
                            {plan.description && (
                              <p className="text-slate-400 text-xs mt-0.5 leading-tight">{plan.description}</p>
                            )}
                          </div>
                        </div>
                        <div
                          className="w-3.5 h-3.5 rounded-full border-2 border-white shadow shrink-0"
                          style={{ backgroundColor: plan.color }}
                          title={`Color: ${plan.color}`}
                        />
                      </div>

                      <div className="mt-3 flex items-baseline gap-1">
                        <span className={`text-3xl font-bold ${s.text}`}>
                          {Number(plan.price_monthly) === 0 ? 'Free' : `$${plan.price_monthly}`}
                        </span>
                        {Number(plan.price_monthly) > 0 && (
                          <span className="text-slate-400 text-xs">/mes</span>
                        )}
                      </div>
                    </div>

                    {/* Limits */}
                    <div className="px-5 py-4 space-y-2.5">
                      {[
                        { icon: 'ri-user-line',       label: 'Usuarios max',  value: unlimUsers(plan.max_users) },
                        { icon: 'ri-file-text-line',  label: 'Contratos max', value: unlimContracts(plan.max_contracts) },
                        { icon: 'ri-hard-drive-line', label: 'Storage',       value: storageLabel(plan.storage_mb) },
                      ].map((lim) => (
                        <div key={lim.label} className="flex items-center gap-2.5">
                          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-slate-100 text-slate-400 shrink-0">
                            <i className={`${lim.icon} text-xs`}></i>
                          </span>
                          <span className="text-slate-500 text-xs flex-1">{lim.label}</span>
                          <span className={`text-xs font-semibold ${s.text}`}>{lim.value}</span>
                        </div>
                      ))}
                    </div>

                    {/* Footer actions */}
                    <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                      <span className={`flex items-center gap-1.5 text-xs font-medium ${plan.is_active ? 'text-emerald-600' : 'text-red-500'}`}>
                        <i className={plan.is_active ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}></i>
                        {plan.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setModalPlan(plan)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                          title="Editar plan"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleToggle(plan)}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                            plan.is_active
                              ? 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                              : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'
                          }`}
                          title={plan.is_active ? 'Desactivar' : 'Activar'}
                        >
                          <i className={plan.is_active ? 'ri-toggle-fill text-sm' : 'ri-toggle-line text-sm'}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add plan card */}
              <button
                onClick={() => setModalPlan(null)}
                className="bg-white rounded-2xl border-2 border-dashed border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 flex flex-col items-center justify-center gap-2 p-8 transition-all cursor-pointer group min-h-[200px]"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 group-hover:bg-blue-100 text-slate-400 group-hover:text-blue-600 transition-colors">
                  <i className="ri-add-line text-lg"></i>
                </span>
                <p className="text-slate-400 group-hover:text-blue-600 text-sm font-medium transition-colors">Nuevo plan</p>
              </button>
            </div>

            {/* Comparison table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                <p className="text-slate-500 text-xs uppercase tracking-wider font-medium">Comparativa de límites</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-xs text-slate-400 font-medium">Límite</th>
                      {plans.map((p) => {
                        const s = PLAN_ICONS[p.id] ?? DEFAULT_STYLE;
                        return (
                          <th key={p.id} className="px-5 py-3 text-center text-xs font-medium">
                            <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${s.badge} ${s.badgeText}`}>{p.name}</span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Precio/mes', getValue: (p: Plan) => Number(p.price_monthly) === 0 ? 'Gratis' : `$${p.price_monthly}` },
                      { label: 'Usuarios',   getValue: (p: Plan) => unlimUsers(p.max_users) },
                      { label: 'Contratos',  getValue: (p: Plan) => unlimContracts(p.max_contracts) },
                      { label: 'Storage',    getValue: (p: Plan) => storageLabel(p.storage_mb) },
                      { label: 'Estado',     getValue: (p: Plan) => p.is_active ? '✅ Activo' : '❌ Inactivo' },
                    ].map((row) => (
                      <tr key={row.label} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                        <td className="px-5 py-3 text-slate-500 text-xs font-medium">{row.label}</td>
                        {plans.map((p) => {
                          const s = PLAN_ICONS[p.id] ?? DEFAULT_STYLE;
                          return (
                            <td key={p.id} className={`px-5 py-3 text-center text-sm font-semibold ${s.text}`}>
                              {row.getValue(p)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Modules per Plan ── */}
        {activeTab === 'modules' && <PlanModulesSection />}
      </div>

      {/* Modal */}
      {modalPlan !== undefined && (
        <PlanModal
          plan={modalPlan}
          onClose={() => setModalPlan(undefined)}
          onSaved={handleSaved}
        />
      )}
    </SuperAdminLayout>
  );
}
